/**
 * SchedulePage.jsx
 * =================
 * Global "Schedule" view — top-level route /schedule in the main Sidebar.
 *
 * Lists projects that are funded OR have a locked multi-unit schedule, so
 * the PM can jump straight to the schedule tab of any active build.
 *
 * Data source: GET /api/pipeline/projects?owner_id=... (existing endpoint),
 * filtered client-side to: stage === 'funded' || has_locked_schedule.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Search, Loader2, BadgeCheck, MapPin, Building2,
  Wand2, ArrowRight, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SchedulePage({ user }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    axios.get(`${API}/pipeline/projects`, { params: { owner_id: user.id } })
      .then(r => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load schedules'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  // Scope: funded OR projects with a locked schedule
  const scoped = useMemo(
    () => projects.filter(p => p.stage === 'funded' || p.has_locked_schedule),
    [projects]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.address || '').toLowerCase().includes(q)
    );
  }, [scoped, search]);

  // Split: with-schedule first, then needs-schedule
  const withSchedule = filtered.filter(p => p.has_locked_schedule);
  const needsSchedule = filtered.filter(p => !p.has_locked_schedule);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="global-schedule-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-6 px-6 rounded-2xl mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <Calendar className="w-7 h-7" /> Schedules
        </h1>
        <p className="text-emerald-100 mt-1 text-sm">
          Funded projects and active builds. Click into any project to view or edit its locked schedule.
        </p>
      </div>

      {/* Search */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex-1 max-w-md relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by project name or address…"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            data-testid="schedule-search"
          />
        </div>
        <div className="text-xs text-slate-500 ml-auto">
          {filtered.length} project{filtered.length === 1 ? '' : 's'}
          {' · '}
          {withSchedule.length} with locked schedule · {needsSchedule.length} need scheduling
        </div>
      </div>

      {loading && (
        <div className="py-20 text-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-16 text-center bg-white">
          <Calendar className="w-10 h-10 mx-auto text-slate-400 mb-2" />
          <h3 className="text-base font-semibold text-slate-900">No active builds yet</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
            Once a project is funded or has a locked schedule, it will appear here for quick access.
          </p>
        </div>
      )}

      {/* With schedule section */}
      {withSchedule.length > 0 && (
        <section className="mb-8" data-testid="with-schedule-section">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Locked schedules ({withSchedule.length})
          </h2>
          <div className="space-y-2">
            {withSchedule.map(p => (
              <ScheduleRow key={p.id} p={p} onClick={() => navigate(`/project/${p.id}?tab=schedule`)} hasSchedule />
            ))}
          </div>
        </section>
      )}

      {/* Needs schedule section */}
      {needsSchedule.length > 0 && (
        <section data-testid="needs-schedule-section">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
            <Wand2 className="w-3.5 h-3.5" /> Funded — ready to schedule ({needsSchedule.length})
          </h2>
          <div className="space-y-2">
            {needsSchedule.map(p => (
              <ScheduleRow key={p.id} p={p} onClick={() => navigate(`/project/${p.id}?tab=schedule`)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ScheduleRow({ p, hasSchedule, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-4 px-4 py-3 rounded-xl border bg-white hover:shadow-md transition-all ${
        hasSchedule ? 'border-emerald-200 hover:border-emerald-400' : 'border-slate-200 hover:border-blue-400'
      }`}
      data-testid={`schedule-row-${p.id}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        hasSchedule ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
      }`}>
        {hasSchedule ? <Lock className="w-5 h-5" /> : <Wand2 className="w-5 h-5" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold text-slate-900 truncate">{p.name || 'Untitled project'}</div>
          {p.stage === 'funded' && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
              <BadgeCheck className="w-3 h-3" /> Funded
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{p.address || 'No address'}</span>
          {p.units > 1 && (
            <>
              <span className="mx-1 text-slate-300">·</span>
              <Building2 className="w-3 h-3" />
              <span>{p.buildings && p.buildings > 1 ? `${p.buildings} buildings, ` : ''}{p.units} units</span>
            </>
          )}
        </div>
      </div>

      <div className="text-right text-xs text-slate-500 shrink-0">
        {hasSchedule ? (
          <>
            <div className="font-semibold text-emerald-700">Locked</div>
            <div className="tabular-nums">{p.last_activity ? formatDate(p.last_activity) : ''}</div>
          </>
        ) : (
          <div className="font-semibold text-amber-700">Tap to schedule</div>
        )}
      </div>

      <ArrowRight className="w-4 h-4 text-slate-400" />
    </button>
  );
}
